/**
 * Estimativa de custos de importação por conta e ordem (via trading).
 * Espelho da planilha "CONTA E ORDEM ... CEO" (abas Estimativa / Memória Cálculo / Siscomex).
 * Valores na moeda da operação; R$ = moeda × taxa fiscal. Alíquotas em % (ex.: 20 = 20%).
 */

export type Regime = "SIMPLES" | "PRESUMIDO" | "REAL";
export type Modal = "MARITIMO" | "AEREO";

export const regimeLabels: Record<Regime, string> = {
  SIMPLES: "Simples Nacional",
  PRESUMIDO: "Lucro Presumido",
  REAL: "Lucro Real",
};

export interface ContaOrdemParams {
  /** Seguro sobre (FOB + frete). */
  insuranceRate: number;
  /** Honorários CVF (entram no "ICMS via trading"). */
  cvfFeeRate: number;
  /** Recolhimento de ICMS (ICMS + fundos). */
  icmsCollectRate: number;
  /** Crédito de ICMS destacado (base por dentro). */
  icmsCreditRate: number;
  /** Créditos de PIS/COFINS — só Lucro Real. */
  pisCreditRate: number;
  cofinsCreditRate: number;
  /** AFRMM = frete R$ × % + fixo (só marítimo). */
  afrmmRate: number;
  afrmmFixedBrl: number;
}

/** Alíquotas padrão de PIS/COFINS-Importação (mesmas do motor de tributos); a tabela do NCM substitui ao informar o NCM. */
export const DEFAULT_PIS_RATE = 2.1;
export const DEFAULT_COFINS_RATE = 9.65;

export const defaultParams: ContaOrdemParams = {
  insuranceRate: 0.2,
  cvfFeeRate: 1.5,
  icmsCollectRate: 1.4,
  icmsCreditRate: 4,
  pisCreditRate: 2.1,
  cofinsCreditRate: 9.65,
  afrmmRate: 8,
  afrmmFixedBrl: 21.2,
};

export interface ContaOrdemItemInput {
  quantity: number;
  unitValue: number;
  /** Peso unitário (kg): rateio do frete. */
  unitWeight: number;
  iiRate: number;
  ipiRate: number;
  pisRate: number;
  cofinsRate: number;
  /** Antidumping do item, na moeda da operação. */
  dumping: number;
  /** Crédito de ICMS / recolhimento do item (%), vindos da parametrização; vazio = parâmetros gerais. */
  icmsCreditRate?: number | null;
  icmsCollectRate?: number | null;
}

export interface ContaOrdemInput {
  exchangeRate: number;
  freight: number;
  regime: Regime;
  items: ContaOrdemItemInput[];
  /** Total das despesas aduaneiras em R$ (rateado pelo valor). */
  expensesBrl: number;
  params: ContaOrdemParams;
}

export interface ContaOrdemValues {
  fob: number;
  freight: number;
  insurance: number;
  cif: number;
  ii: number;
  ipi: number;
  pis: number;
  cofins: number;
  dumping: number;
  icms: number;
  taxes: number;
  expenses: number;
  ipiDifference: number;
  disbursement: number;
  icmsCredit: number;
  ipiCredit: number;
  pisCredit: number;
  cofinsCredit: number;
  finalCost: number;
  // Internos (não vão para a impressão)
  icmsCost: number;
  /** Base "por dentro" do ICMS na trading (crédito/recolhimento incidem sobre ela). */
  icmsBase: number;
  cvfFee: number;
  presumedCredit: number;
}

export interface ContaOrdemItemResult extends ContaOrdemValues {
  quantity: number;
  disbursementPerUnit: number;
  finalCostPerUnit: number;
}

export interface ContaOrdemResult {
  items: ContaOrdemItemResult[];
  totals: ContaOrdemValues;
  quantity: number;
}

const valueKeys: (keyof ContaOrdemValues)[] = [
  "fob", "freight", "insurance", "cif", "ii", "ipi", "pis", "cofins", "dumping", "icms", "taxes", "expenses",
  "ipiDifference", "disbursement", "icmsCredit", "ipiCredit", "pisCredit", "cofinsCredit", "finalCost",
  "icmsCost", "cvfFee", "presumedCredit", "icmsBase",
];

const pct = (rate: number) => (rate || 0) / 100;
const safe = (value: number) => (Number.isFinite(value) ? value : 0);

/**
 * Crédito e recolhimento médios (ponderados pela base) — para exibir quando os itens têm alíquotas próprias.
 * Sem base, devolve os parâmetros.
 */
export const effectiveIcmsParams = (params: ContaOrdemParams, totals: ContaOrdemValues): ContaOrdemParams =>
  totals.icmsBase > 0
    ? { ...params, icmsCreditRate: (-totals.icmsCredit / totals.icmsBase) * 100, icmsCollectRate: (totals.icmsCost / totals.icmsBase) * 100 }
    : params;

/** Seguro total = (FOB + frete) × taxa. */
export const insuranceOf = (fob: number, freight: number, params: ContaOrdemParams) =>
  (fob + freight) * pct(params.insuranceRate);

export const calculateContaOrdem = (input: ContaOrdemInput): ContaOrdemResult => {
  const { params, exchangeRate } = input;
  const items = input.items;
  const fobs = items.map((i) => Math.max(i.quantity, 0) * Math.max(i.unitValue, 0));
  const weights = items.map((i) => Math.max(i.quantity, 0) * Math.max(i.unitWeight, 0));
  const totalFob = fobs.reduce((a, b) => a + b, 0);
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const insuranceTotal = insuranceOf(totalFob, input.freight, params);
  const expensesTotal = exchangeRate > 0 ? input.expensesBrl / exchangeRate : 0;
  const lucroReal = input.regime === "REAL";
  const fallbackShare = items.length ? 1 / items.length : 0;

  const results = items.map((item, index): ContaOrdemItemResult => {
    const valueShare = totalFob > 0 ? fobs[index] / totalFob : fallbackShare;
    const weightShare = totalWeight > 0 ? weights[index] / totalWeight : valueShare;

    const credit = pct(item.icmsCreditRate ?? params.icmsCreditRate);
    const collect = pct(item.icmsCollectRate ?? params.icmsCollectRate);
    const grossUp = credit < 1 ? 1 / (1 - credit) : 0;

    const fob = fobs[index];
    const freight = input.freight * weightShare;
    const insurance = insuranceTotal * valueShare;
    const cif = fob + freight + insurance;
    const ii = cif * pct(item.iiRate);
    const ipi = (cif + ii) * pct(item.ipiRate);
    const pis = cif * pct(item.pisRate);
    const cofins = cif * pct(item.cofinsRate);
    const dumping = Math.max(item.dumping || 0, 0);
    const expenses = expensesTotal * valueShare;

    // Base "por dentro" do ICMS na trading (sem IPI, como na planilha)
    const base = cif + ii + pis + cofins + expenses;
    const grossBase = base * grossUp;
    const icms = grossBase * (pct(params.cvfFeeRate) + collect);
    const ipiDifference = (base + dumping) * grossUp * pct(item.ipiRate) - ipi;
    const taxes = ii + ipi + pis + cofins + dumping + icms;
    const disbursement = cif + taxes + expenses + ipiDifference;

    const icmsCredit = -grossBase * credit;
    const ipiCredit = -(ipi + ipiDifference);
    const pisCredit = lucroReal ? -cif * pct(params.pisCreditRate) : 0;
    const cofinsCredit = lucroReal ? -cif * pct(params.cofinsCreditRate) : 0;
    const finalCost = disbursement + icmsCredit + ipiCredit + pisCredit + cofinsCredit;

    const icmsCost = grossBase * collect;
    const quantity = Math.max(item.quantity, 0);

    return {
      quantity,
      fob,
      freight,
      insurance,
      cif,
      ii,
      ipi,
      pis,
      cofins,
      dumping,
      icms,
      taxes,
      expenses,
      ipiDifference,
      disbursement,
      icmsCredit,
      ipiCredit,
      pisCredit,
      cofinsCredit,
      finalCost,
      icmsCost,
      icmsBase: grossBase,
      cvfFee: icms - icmsCost,
      presumedCredit: grossBase * (credit - collect),
      disbursementPerUnit: quantity > 0 ? disbursement / quantity : 0,
      finalCostPerUnit: quantity > 0 ? finalCost / quantity : 0,
    };
  });

  const totals = Object.fromEntries(
    valueKeys.map((key) => [key, safe(results.reduce((sum, r) => sum + r[key], 0))])
  ) as unknown as ContaOrdemValues;

  return { items: results, totals, quantity: results.reduce((sum, r) => sum + r.quantity, 0) };
};

// ---- Despesas aduaneiras (R$) ----

/** Taxa Siscomex: registro 115,67 + valor por adição conforme a faixa (aba Siscomex). */
export const siscomexFee = (additions: number) => {
  const n = Math.max(Math.floor(additions || 0), 0);
  let total = 115.67;
  for (let i = 1; i <= n; i++) {
    if (i <= 2) total += 38.56;
    else if (i <= 5) total += 30.85;
    else if (i <= 10) total += 23.14;
    else if (i <= 20) total += 15.42;
    else if (i <= 50) total += 7.71;
    else total += 3.86;
  }
  return Math.round(total * 100) / 100;
};

export const afrmmFee = (freightBrl: number, modal: Modal, params: ContaOrdemParams) =>
  modal === "MARITIMO" && freightBrl > 0 ? freightBrl * pct(params.afrmmRate) + params.afrmmFixedBrl : 0;

export interface StorageRule {
  /** % do CIF (R$) no 1º período. */
  firstPeriodRate: number;
  /** Dias do 1º período (informativo). */
  firstPeriodDays?: number;
  /** Levante e pesagem (R$ fixo). */
  fixedBrl: number;
  /** % do CIF (R$) por período adicional. */
  extraPeriodRate: number;
}

export interface Port extends StorageRule {
  id: string;
  name: string;
  modal: Modal | "ALL";
  /** Sem tarifa na planilha: armazenagem manual. */
  noData?: boolean;
}

export const CUSTOM_PORT = "PERSONALIZADO";

/** Tabela de armazenagem da planilha (Itajaí/Itapoá/Navegantes/Guarulhos). Santos e Viracopos sem tarifa. */
export const PORTS: Port[] = [
  { id: CUSTOM_PORT, name: "Personalizado (% CIF + fixo)", modal: "ALL", firstPeriodRate: 0.42, fixedBrl: 13000, extraPeriodRate: 0 },
  { id: "ITAPOA", name: "Itapoá", modal: "MARITIMO", firstPeriodRate: 0.52, firstPeriodDays: 6, fixedBrl: 424, extraPeriodRate: 0.348 },
  { id: "NAVEGANTES", name: "Navegantes", modal: "MARITIMO", firstPeriodRate: 0.45, firstPeriodDays: 5, fixedBrl: 558, extraPeriodRate: 0.33 },
  { id: "ITAJAI", name: "Itajaí", modal: "MARITIMO", firstPeriodRate: 1.33, firstPeriodDays: 15, fixedBrl: 600, extraPeriodRate: 0.021 },
  { id: "SANTOS", name: "Santos", modal: "MARITIMO", firstPeriodRate: 0, fixedBrl: 0, extraPeriodRate: 0, noData: true },
  { id: "GUARULHOS", name: "Guarulhos", modal: "AEREO", firstPeriodRate: 0.8, fixedBrl: 0, extraPeriodRate: 0 },
  { id: "VIRACOPOS", name: "Viracopos", modal: "AEREO", firstPeriodRate: 0, fixedBrl: 0, extraPeriodRate: 0, noData: true },
];

/** Armazenagem = CIF R$ × 1º período + fixo + CIF R$ × período adicional × nº de períodos extras. */
export const storageFee = (cifBrl: number, rule: StorageRule, extraPeriods: number) =>
  cifBrl * pct(rule.firstPeriodRate) + rule.fixedBrl + cifBrl * pct(rule.extraPeriodRate) * Math.max(extraPeriods || 0, 0);
